/**
 * ELL Store Self Scan - Main Application Entry Point
 *
 * This file initializes the application and wires up event handlers.
 * Business logic is delegated to specialized modules.
 */
import { VERSION } from './version.js';
import '@georapbox/a-tab-group/dist/a-tab-group.js';
import '@georapbox/web-share-element/dist/web-share-defined.js';
import '@georapbox/files-dropzone-element/dist/files-dropzone-defined.js';
import '@georapbox/resize-observer-element/dist/resize-observer-defined.js';
import '@georapbox/modal-element/dist/modal-element-defined.js';
import '@georapbox/alert-element/dist/alert-element-defined.js';

import { ACCEPTED_MIME_TYPES, DEBOUNCE_DELAY, SETTINGS_DEBOUNCE } from './constants.js';
import { getSettings, setSettings, clearSensitiveData } from './services/storage.js';
import { debounce } from './utils/debounce.js';
import { log } from './utils/log.js';
import { isDialogElementSupported } from './utils/isDialogElementSupported.js';
import { resizeScanFrame } from './helpers/resizeScanFrame.js';
import { BarcodeReader } from './helpers/BarcodeReader.js';
import { toggleTorchButtonStatus } from './helpers/toggleTorchButtonStatus.js';
import { toastify } from './helpers/toastify.js';
import { VideoCapture } from './components/video-capture.js';
import './components/clipboard-copy.js';
import './components/bs-result.js';
import './components/bs-settings.js';
import './components/bs-history.js';

// State management
import {
  setBarcodeReader,
  getBarcodeReader,
  startScanning,
  stopScanning,
  shouldContinueScanning,
  getClientId,
  setClientId,
  getDossierId
} from './state.js';

// Modules
import { initUI, displayCameraError, showAlert } from './modules/ui.js';
import {
  initAuth,
  handleValidateCustomer,
  handleEditCustomer,
  fetchAndDisplayCustomerInfo,
  updateCustomerDisplay,
  handleClientIdSubmit,
  handleConfirmYes,
  handleConfirmNo
} from './modules/auth.js';
import {
  initDossier,
  loadDossierProducts,
  handleValidateDossier,
  handleEditDossier,
  handleEndSession
} from './modules/dossier.js';
import {
  initScanner,
  scan,
  handleScanButtonClick,
  handleTabShow,
  handleFileSelect,
  handleFileDrop,
  handleVideoCaptureResize,
  handleTorchButtonClick,
  handleCameraSelectChange,
  handleDocumentVisibilityChange
} from './modules/scanner.js';
import { initProductConfirm } from './modules/productConfirm.js';

/**
 * Main application initialization
 */
(async function () {
  // DOM Elements
  const tabGroupEl = document.querySelector('a-tab-group');
  const videoCaptureEl = document.querySelector('video-capture');
  const bsSettingsEl = document.querySelector('bs-settings');
  const bsHistoryEl = document.querySelector('bs-history');
  const cameraPanel = document.getElementById('cameraPanel');
  const scanInstructionsEl = document.getElementById('scanInstructions');
  const scanBtn = document.getElementById('scanBtn');
  const dropzoneEl = document.getElementById('dropzone');
  const resizeObserverEl = document.querySelector('resize-observer');
  const scanFrameEl = document.getElementById('scanFrame');
  const torchButton = document.getElementById('torchButton');
  const globalActionsEl = document.getElementById('globalActions');
  const historyBtn = document.getElementById('historyBtn');
  const historyDialog = document.getElementById('historyDialog');
  const settingsBtn = document.getElementById('settingsBtn');
  const settingsDialog = document.getElementById('settingsDialog');
  const settingsForm = document.getElementById('settingsForm');
  const cameraSelect = document.getElementById('cameraSelect');
  const customerNumberInput = document.getElementById('customer-number');
  const validateCustomerBtn = document.getElementById('validate-customer-btn');
  const customerInfoDiv = document.getElementById('customer-info');
  const dossierInfoDiv = document.getElementById('dossier-info');
  const alertsContainer = document.getElementById('alerts-container');
  const endSessionBtn = document.getElementById('end-session-btn');
  const endSessionBtnTop = document.getElementById('end-session-btn-top');
  const dossierInputContainer = document.getElementById('dossier-input-container');
  const dossierNumberInput = document.getElementById('dossier-number');
  const validateDossierBtn = document.getElementById('validate-dossier-btn');
  const dossierDisplayContainer = document.getElementById('dossier-display-container');
  const dossierDisplay = document.getElementById('dossier-display');
  const editDossierBtn = document.getElementById('edit-dossier-btn');

  // Initialize modules
  initUI({ alertsContainer });

  initAuth({
    customerNumberInput,
    validateCustomerBtn,
    customerInfoDiv,
    dossierInfoDiv
  });

  initDossier({
    dossierInputContainer,
    dossierNumberInput,
    dossierDisplayContainer,
    dossierDisplay
  });

  // Initialize product confirmation module
  initProductConfirm();

  // Handle saved dossier ID after DOM is ready
  const handleSavedDossier = () => {
    const savedDossierId = getDossierId();
    if (savedDossierId) {
      loadDossierProducts(savedDossierId);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleSavedDossier, { once: true });
  } else {
    handleSavedDossier();
  }

  // Enable dialogs if supported
  if (isDialogElementSupported()) {
    globalActionsEl?.removeAttribute('hidden');
    historyDialog?.removeAttribute('hidden');
    settingsDialog?.removeAttribute('hidden');
  }

  // Initialize barcode reader
  const { barcodeReaderError } = await BarcodeReader.setup();

  if (barcodeReaderError) {
    const alertEl = document.getElementById('barcodeReaderError');
    stopScanning();
    globalActionsEl?.setAttribute('hidden', '');
    tabGroupEl?.setAttribute('hidden', '');
    alertEl?.setAttribute('open', '');
    return;
  }

  const supportedBarcodeFormats = await BarcodeReader.getSupportedFormats();
  const [, settings] = await getSettings();
  const initialFormats = settings?.formats || supportedBarcodeFormats;
  const barcodeReader = await BarcodeReader.create(initialFormats);
  setBarcodeReader(barcodeReader);

  // Video capture setup
  const videoCaptureShadowRoot = videoCaptureEl?.shadowRoot;
  const videoCaptureVideoEl = videoCaptureShadowRoot?.querySelector('video');
  const videoCaptureActionsEl = videoCaptureShadowRoot?.querySelector('[part="actions-container"]');

  // Initialize scanner module
  initScanner({
    videoCaptureEl,
    videoCaptureVideoEl,
    videoCaptureActionsEl,
    scanBtn,
    scanFrameEl,
    scanInstructionsEl,
    cameraPanel,
    dropzoneEl,
    bsHistoryEl,
    cameraSelect,
    torchButton
  });

  dropzoneEl.accept = ACCEPTED_MIME_TYPES.join(',');
  bsSettingsEl.supportedFormats = supportedBarcodeFormats;

  // Video capture event handlers
  videoCaptureEl.addEventListener(
    'video-capture:video-play',
    async evt => {
      scanFrameEl?.removeAttribute('hidden');
      resizeScanFrame(evt.detail.video, scanFrameEl);
      scan();

      const trackSettings = evt.target.getTrackSettings();
      const trackCapabilities = evt.target.getTrackCapabilities();
      const zoomLevelEl = document.getElementById('zoomLevel');

      // Torch control
      if (trackCapabilities?.torch) {
        torchButton?.addEventListener('click', handleTorchButtonClick);
        torchButton?.removeAttribute('hidden');

        if (videoCaptureEl.hasAttribute('torch')) {
          toggleTorchButtonStatus({ el: torchButton, isTorchOn: true });
        }
      }

      // Zoom controls
      if (trackSettings?.zoom && trackCapabilities?.zoom) {
        const zoomControls = document.getElementById('zoomControls');
        const minZoom = trackCapabilities?.zoom?.min || 0;
        const maxZoom = trackCapabilities?.zoom?.max || 10;
        let currentZoom = trackSettings?.zoom || 1;

        const handleZoomControlsClick = evt => {
          const zoomInBtn = evt.target.closest('[data-action="zoom-in"]');
          const zoomOutBtn = evt.target.closest('[data-action="zoom-out"]');

          if (zoomInBtn && currentZoom < maxZoom) {
            currentZoom += 0.5;
          }

          if (zoomOutBtn && currentZoom > minZoom) {
            currentZoom -= 0.5;
          }

          zoomLevelEl.textContent = currentZoom.toFixed(1);
          videoCaptureEl.zoom = currentZoom;
        };

        zoomControls?.addEventListener('click', handleZoomControlsClick);
        zoomControls?.removeAttribute('hidden');
        zoomLevelEl.textContent = currentZoom.toFixed(1);
      }

      // Camera select
      const videoInputDevices = await VideoCapture.getVideoInputDevices();

      videoInputDevices.forEach((device, index) => {
        const option = document.createElement('option');
        option.value = device.deviceId;
        option.textContent = device.label || `Camera ${index + 1}`;
        cameraSelect.appendChild(option);
      });

      if (videoInputDevices.length > 1) {
        cameraSelect?.addEventListener('change', handleCameraSelectChange);
        cameraSelect?.removeAttribute('hidden');
      }
    },
    { once: true }
  );

  videoCaptureEl.addEventListener(
    'video-capture:error',
    evt => {
      const error = evt.detail.error;

      if (error.name === 'NotFoundError') {
        return;
      }

      displayCameraError(cameraPanel, error);
    },
    { once: true }
  );

  VideoCapture.defineCustomElement();

  // Settings handlers
  function handleSettingsButtonClick() {
    settingsDialog.open = true;
  }

  async function handleSettingsFormChange(evt) {
    evt.preventDefault();

    const newSettings = {};
    const formData = new FormData(settingsForm);
    const generalSettings = formData.getAll('general-settings');
    const formatsSettings = formData.getAll('formats-settings');

    generalSettings.forEach(value => (newSettings[value] = true));
    newSettings.formats = formatsSettings;
    setSettings(newSettings);

    if (evt.target.name === 'formats-settings') {
      const newBarcodeReader = await BarcodeReader.create(formatsSettings);
      setBarcodeReader(newBarcodeReader);
    }
  }

  // History handlers
  function handleHistoryButtonClick() {
    historyDialog.open = true;
  }

  function handleHistorySuccess(evt) {
    const { type, message } = evt.detail;
    if (type === 'add') {
      toastify(message, { variant: 'success' });
    }
  }

  function handleHistoryError(evt) {
    const { type, message } = evt.detail;
    if (type === 'remove' || type === 'empty') {
      historyDialog?.hide();
    }
    toastify(message, { variant: 'danger' });
  }

  // Document handlers
  function handleDocumentKeyDown(evt) {
    if (evt.key === 'Escape') {
      const cameraTabSelected = tabGroupEl.querySelector('#cameraTab').hasAttribute('selected');
      const scanBtnVisible = !scanBtn.hidden;
      const settingsDialogOpen = settingsDialog.hasAttribute('open');
      const historyDialogOpen = historyDialog.hasAttribute('open');
      const anyDialogOpen = settingsDialogOpen || historyDialogOpen;

      if (!scanBtnVisible || !cameraTabSelected || anyDialogOpen) {
        return;
      }

      scanBtn.click();
    }
  }

  function handleVisibilityChange() {
    if (!tabGroupEl) return;
    const selectedTab = tabGroupEl.querySelector('[selected]');
    const tabId = selectedTab?.getAttribute('id');
    handleDocumentVisibilityChange(tabId);
  }

  // Compact view toggle
  const toggleViewBtn = document.getElementById('toggle-view-btn');
  const scannedItemsContainer = document.getElementById('scanned-items-container');
  const iconCompact = toggleViewBtn?.querySelector('.icon-compact');
  const iconDetailed = toggleViewBtn?.querySelector('.icon-detailed');

  toggleViewBtn?.addEventListener('click', () => {
    scannedItemsContainer?.classList.toggle('compact-mode');
    const isCompact = scannedItemsContainer?.classList.contains('compact-mode');

    // Toggle icons
    if (iconCompact && iconDetailed) {
      iconCompact.hidden = isCompact;
      iconDetailed.hidden = !isCompact;
    }

    // Save preference
    localStorage.setItem('compactViewEnabled', isCompact ? 'true' : 'false');
  });

  // Restore compact view preference
  if (localStorage.getItem('compactViewEnabled') === 'true') {
    scannedItemsContainer?.classList.add('compact-mode');
    if (iconCompact && iconDetailed) {
      iconCompact.hidden = true;
      iconDetailed.hidden = false;
    }
  }

  // Event listeners
  endSessionBtn?.addEventListener('click', handleEndSession);
  endSessionBtnTop?.addEventListener('click', handleEndSession);
  validateDossierBtn?.addEventListener('click', handleValidateDossier);
  editDossierBtn?.addEventListener('click', handleEditDossier);
  scanBtn?.addEventListener('click', handleScanButtonClick);
  tabGroupEl?.addEventListener('a-tab-show', debounce(handleTabShow, DEBOUNCE_DELAY));
  dropzoneEl?.addEventListener('files-dropzone-drop', handleFileDrop);
  resizeObserverEl?.addEventListener('resize-observer:resize', handleVideoCaptureResize);
  settingsBtn?.addEventListener('click', handleSettingsButtonClick);
  settingsForm?.addEventListener('change', debounce(handleSettingsFormChange, SETTINGS_DEBOUNCE));
  historyBtn?.addEventListener('click', handleHistoryButtonClick);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('keydown', handleDocumentKeyDown);
  document.addEventListener('bs-history-success', handleHistorySuccess);
  document.addEventListener('bs-history-error', handleHistoryError);

  // Display version in footer
  const versionEl = document.getElementById('app-version');
  if (versionEl) {
    versionEl.textContent = `v${VERSION}`;
  }
})();

/**
 * Client ID Gate - Modal for initial client selection with confirmation step
 */
document.addEventListener('DOMContentLoaded', () => {
  // Modal elements
  const clientIdModal = document.getElementById('client-id-modal');
  const mainContent = document.getElementById('main-content');
  const clientIdInput = document.getElementById('client-id-input');
  const submitClientIdBtn = document.getElementById('submit-client-id-btn');
  const clientIdError = document.getElementById('client-id-error');

  // Step elements
  const clientIdStep = document.getElementById('client-id-step');
  const confirmStep = document.getElementById('confirm-identity-step');
  const confirmNameEl = document.getElementById('confirm-client-name');
  const confirmVatEl = document.getElementById('confirm-client-vat');
  const confirmYesBtn = document.getElementById('confirm-yes-btn');
  const confirmNoBtn = document.getElementById('confirm-no-btn');

  // Customer display elements
  const customerNumberInput = document.getElementById('customer-number');
  const editValidateBtn = document.getElementById('customer-edit-validate-btn');

  const storedClientId = getClientId();

  // Show/hide modal based on stored client
  if (storedClientId) {
    clientIdModal.hidden = true;
    mainContent.hidden = false;
  } else {
    clientIdModal.hidden = false;
    mainContent.hidden = true;
  }

  // Elements object for auth functions
  const modalElements = {
    modal: clientIdModal,
    mainContent,
    clientIdInput,
    errorEl: clientIdError,
    clientIdStep,
    confirmStep,
    confirmNameEl,
    confirmVatEl
  };

  // Client ID submission handler - validates and shows confirmation
  const onClientIdSubmit = async () => {
    await handleClientIdSubmit(clientIdInput.value, modalElements);
  };

  // Confirmation handlers
  const onConfirmYes = async () => {
    const success = await handleConfirmYes(modalElements);
    if (success) {
      updateCustomerDisplay();
    }
  };

  const onConfirmNo = () => {
    handleConfirmNo(modalElements);
  };

  // Event listeners for modal
  submitClientIdBtn?.addEventListener('click', onClientIdSubmit);
  confirmYesBtn?.addEventListener('click', onConfirmYes);
  confirmNoBtn?.addEventListener('click', onConfirmNo);

  clientIdInput?.addEventListener('keypress', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onClientIdSubmit();
    }
  });

  // Customer edit button - shows modal for re-authentication
  editValidateBtn?.addEventListener('click', () => {
    // Reset modal to step 1
    if (clientIdStep) {
      clientIdStep.hidden = false;
    }
    if (confirmStep) {
      confirmStep.hidden = true;
    }
    if (clientIdInput) {
      clientIdInput.value = '';
      clientIdInput.focus();
    }

    // Show the modal
    if (clientIdModal) {
      clientIdModal.hidden = false;
    }
  });

  // Initial display update
  setTimeout(() => {
    updateCustomerDisplay();
  }, 100);
});
